import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Train, Building2, Users, FileText, Calendar, ArrowLeft, ArrowRight, Check } from 'lucide-react';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: 'Set Up Your First Facility',
      description: 'Add your repair shop, mobile unit, or mini-shop location',
      icon: Building2,
      action: 'Add Facility',
      route: '/facility-management'
    },
    {
      title: 'Invite Your Team',
      description: 'Add operators, managers, and technicians to your account',
      icon: Users,
      action: 'Invite Users',
      route: '/user-management'
    },
    {
      title: 'Create a Service Request',
      description: 'Track railcar maintenance from request to completion',
      icon: FileText,
      action: 'Create Request',
      route: '/service-requests/add'
    },
    {
      title: 'Schedule Work',
      description: 'Assign jobs to your crew with priorities and dates',
      icon: Calendar,
      action: 'Schedule Work',
      route: '/dashboard'
    },
    {
      title: 'Switch Between Facilities',
      description: 'Easily toggle between locations from the top navigation',
      icon: Building2,
      action: 'Got It',
      route: '/dashboard'
    }
  ];

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/facility-management');
    }
  };

  const handleSkip = () => {
    navigate('/facility-management');
  };

  const handleAction = () => {
    navigate(currentStepData.route);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-border px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#005EF8] flex items-center justify-center">
            <Train className="w-6 h-6 text-white" />
          </div>
          <span className="text-[#323033] text-xl font-semibold">Onsite Rail Solutions</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-lg">
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {steps.map((step, index) => (
              <div
                key={step.route}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentStep ? 'bg-[#005EF8]' : index < currentStep ? 'bg-[#005EF8]/50' : 'bg-[#D3D5D4]'
                }`}
              />
            ))}
          </div>

          {/* Step content */}
          <div className="text-center">
            <div className="w-20 h-20 bg-[#005EF8]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Icon className="w-10 h-10 text-[#005EF8]" />
            </div>

            <h1 className="text-2xl font-bold text-[#323033] mb-3">
              Step {currentStep + 1}: {currentStepData.title}
            </h1>
            <p className="text-[#5A5A5A] mb-8">{currentStepData.description}</p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                onClick={handleSkip}
                className="order-2 sm:order-1"
                data-testid="onboarding-skip-btn"
              >
                Skip Tour
              </Button>
              <Button
                onClick={handleAction}
                className="bg-[#005EF8] hover:bg-[#0047C2] text-white order-1 sm:order-2"
                data-testid="onboarding-action-btn"
              >
                {currentStepData.action}
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-12">
            <Button
              variant="ghost"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="text-[#5A5A5A]"
              data-testid="onboarding-prev-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            <Button
              variant="ghost"
              onClick={handleNext}
              className="text-[#5A5A5A]"
              data-testid="onboarding-next-btn"
            >
              {currentStep === steps.length - 1 ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Finish Tour
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OnboardingPage;
